import { Controller } from "@hotwired/stimulus"
import { fireNumber, monthsToTarget, project, realReturn } from "../fire_math"
import { track } from "./analytics_controller"
import { currencyFromQuery, drawChart, duration, line, money, monthYear, nextCurrency, percent, renderLevers, renderSteps, saveQuery, shortDuration, yearLabels } from "../fire_view"

export default class extends Controller {
  static targets = ["currency", "form", "advanced", "real", "number", "multiple", "duration", "date", "full", "fullNote", "canvas", "milestones", "levers", "milestoneTemplate", "leverTemplate"]

  connect() {
    this.currency = currencyFromQuery()
    this.currencyTargets.forEach((target) => { target.textContent = this.currency.symbol })
    this.update(false)
  }

  cycle() {
    this.currency = nextCurrency(this.currency)
    this.currencyTargets.forEach((target) => { target.textContent = this.currency.symbol })
    this.update(true)
  }

  money(value) {
    return money(value, this.currency)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.chart?.destroy()
  }

  submit(event) {
    event.preventDefault()
  }

  change() {
    this.update(true)
  }

  read(name) {
    const value = parseFloat(this.formTarget.elements[name].value)
    return Number.isFinite(value) ? value : null
  }

  inputs() {
    return {
      spending: Math.max(0, this.read("spending") || 0),
      income: Math.max(0, this.read("income") || 0),
      portfolio: Math.max(0, this.read("portfolio") || 0),
      monthly: Math.max(0, this.read("monthly") || 0),
      nominal: (this.read("return") || 0) / 100,
      withdrawal: (this.read("withdrawal") || 0) / 100,
      inflation: (this.read("inflation") || 0) / 100,
      fees: (this.read("fees") || 0) / 100,
      raise: (this.read("raise") || 0) / 100,
      tax: Math.min(0.99, Math.max(0, (this.read("tax") || 0) / 100)),
      age: this.read("age")
    }
  }

  baristaNumber(spending, income, withdrawal, tax) {
    return fireNumber(Math.max(0, spending - income), withdrawal, tax)
  }

  reach(plan, target) {
    return target > 0 ? monthsToTarget({ ...plan, target }) : 0
  }

  update(record) {
    const inputs = this.inputs()
    const annual = realReturn(inputs)
    const full = fireNumber(inputs.spending, inputs.withdrawal, inputs.tax)
    const target = this.baristaNumber(inputs.spending, inputs.income, inputs.withdrawal, inputs.tax)
    const plan = { portfolio: inputs.portfolio, monthly: inputs.monthly, annual, raise: inputs.raise }
    const valid = full > 0
    const months = valid ? this.reach(plan, target) : null
    const fullMonths = valid ? this.reach(plan, full) : null
    const reference = fullMonths ?? months
    const horizon = reference === null ? 40 : Math.min(60, Math.max(10, Math.ceil(reference / 12) + 5))
    const points = project({ ...plan, months: horizon * 12 })
    const context = { inputs, annual, valid, full, target, plan, months, fullMonths, points }

    this.realTarget.textContent = `About ${percent(annual)} a year after inflation${inputs.fees > 0 ? " and fees" : ""}.`
    this.renderSummary(context)
    this.renderChart(context, horizon)
    this.renderMilestones(context)
    this.renderLevers(context)

    if (record) this.record(months)
  }

  record(months) {
    saveQuery(this.formTarget, this.currency)

    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      track("barista-fire-run", { currency: this.currency.code, years: months === null ? "never" : Math.floor(months / 12), advanced: this.advancedTarget.open })
    }, 1500)
  }

  ageAt(months) {
    const { age } = this.inputs()
    return age === null ? null : Math.floor(age + months / 12)
  }

  when(months) {
    const age = this.ageAt(months)
    return `Around ${monthYear(months)}${age === null ? "" : `, at ${age}`}`
  }

  renderSummary({ inputs, valid, full, target, months, fullMonths }) {
    if (!valid) {
      this.numberTarget.textContent = "—"
      this.multipleTarget.textContent = "Add your spending and a withdrawal rate"
    } else if (target === 0) {
      this.numberTarget.textContent = this.money(0)
      this.multipleTarget.textContent = "Your part-time income covers all your spending"
    } else {
      this.numberTarget.textContent = this.money(target)
      this.multipleTarget.textContent = `Covers ${this.money(inputs.spending - inputs.income)} a year. The job pays the rest`
    }

    if (!valid) {
      this.durationTarget.textContent = "—"
      this.dateTarget.textContent = ""
    } else if (months === null) {
      this.durationTarget.textContent = "Out of reach"
      this.dateTarget.textContent = "Save more or expect a higher return"
    } else if (months === 0) {
      this.durationTarget.textContent = "You're there"
      this.dateTarget.textContent = "You could go part-time today"
    } else {
      this.durationTarget.textContent = shortDuration(months)
      this.dateTarget.textContent = this.when(months)
    }

    this.fullTarget.textContent = valid ? this.money(full) : "—"
    if (!valid) this.fullNoteTarget.textContent = ""
    else if (fullMonths === null) this.fullNoteTarget.textContent = "Out of reach at this pace"
    else if (fullMonths === 0) this.fullNoteTarget.textContent = "You already have it"
    else if (months !== null && fullMonths > months) this.fullNoteTarget.textContent = `In ${shortDuration(fullMonths)}, ${duration(fullMonths - months)} after Barista FIRE`
    else this.fullNoteTarget.textContent = `In ${shortDuration(fullMonths)}`
  }

  renderChart({ full, target, points }, horizon) {
    const yearly = points.filter((point) => point.month % 12 === 0)
    this.chart = drawChart(this.chart, this.canvasTarget, {
      labels: yearLabels(points),
      horizon,
      currency: this.currency,
      datasets: [
        line("Portfolio", yearly.map((point) => point.value), "#00d6b4", { backgroundColor: "rgba(0, 214, 180, 0.08)", fill: "origin", pointHoverRadius: 4 }),
        line("Money put in", yearly.map((point) => point.invested), "rgba(255, 255, 255, 0.4)", { borderDash: [6, 6] }),
        line("Barista number", yearly.map(() => target), "#ffb22b"),
        line("FIRE number", yearly.map(() => full), "#e14eca")
      ]
    })
  }

  away(months) {
    const age = this.ageAt(months)
    return `${duration(months)} from today${age === null ? "" : `, age ${age}`}`
  }

  renderMilestones({ inputs, valid, full, target, plan, months, fullMonths }) {
    const share = target > 0 ? Math.round(Math.min(1, inputs.portfolio / target) * 100) : 100
    const rows = [{ kind: "today", when: "Today", title: "Where you are now", detail: valid ? `${share}% of your Barista FIRE number` : "Fill in the form to see your plan", amount: inputs.portfolio }]

    if (valid && months > 0) {
      const half = monthsToTarget({ ...plan, target: target / 2 })
      if (half > 0) rows.push({ kind: "milestone", month: half, title: "Halfway to Barista FIRE", detail: this.away(half), amount: target / 2 })
      rows.push({ kind: "barista", month: months, title: "Reach Barista FIRE and go part-time", detail: `You earn ${this.money(inputs.income)} a year and the portfolio covers the rest`, amount: target })
    }

    if (valid && fullMonths > 0) {
      rows.push({ kind: "fire", month: fullMonths, title: "Full FIRE if you keep working full time", detail: this.away(fullMonths), amount: full })
    } else if (valid && fullMonths === null) {
      rows.push({ kind: "fire", month: Infinity, when: "Not yet", title: "Full FIRE", detail: "Out of reach at this pace", amount: full })
    }

    renderSteps(this.milestonesTarget, this.milestoneTemplateTarget, rows.sort((first, second) => (first.month || 0) - (second.month || 0)), this.currency)
  }

  renderLevers({ inputs, valid, target, plan, months }) {
    if (!valid || target === 0) {
      this.leversTarget.replaceChildren()
      return
    }

    const extraIncome = 5000 * this.currency.scale
    const extraSaving = 250 * this.currency.scale
    const spendingCut = 3000 * this.currency.scale
    const lowerWithdrawal = inputs.withdrawal - 0.005
    const withIncome = this.baristaNumber(inputs.spending, inputs.income + extraIncome, inputs.withdrawal, inputs.tax)
    const withSpending = this.baristaNumber(inputs.spending - spendingCut, inputs.income, inputs.withdrawal, inputs.tax)
    const levers = [
      { label: `Earn ${this.money(extraIncome)} more a year part-time`, detail: `Barista number drops to ${this.money(withIncome)}`, months: this.reach(plan, withIncome) },
      { label: `Save ${this.money(extraSaving)} more a month`, detail: `${this.money(inputs.monthly + extraSaving)} a month`, months: monthsToTarget({ ...plan, monthly: inputs.monthly + extraSaving, target }) },
      inputs.spending > spendingCut && { label: `Spend ${this.money(spendingCut)} less a year`, detail: `Barista number drops to ${this.money(withSpending)}`, months: this.reach(plan, withSpending) },
      lowerWithdrawal > 0 && { label: `Withdraw ${percent(lowerWithdrawal)} instead`, detail: `Barista number rises to ${this.money(this.baristaNumber(inputs.spending, inputs.income, lowerWithdrawal, inputs.tax))}`, months: this.reach(plan, this.baristaNumber(inputs.spending, inputs.income, lowerWithdrawal, inputs.tax)) }
    ].filter(Boolean)

    renderLevers(this.leversTarget, this.leverTemplateTarget, months, levers)
  }
}
